import { useEffect, useState, useMemo } from "react";
import { Skeleton } from "../../components/ui";
import swal from "../../utils/swal";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Newspaper,
  SearchX,
  Plus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDeletePost, useGetAllPosts } from "../../query/postQuery";
import { useGetAllClasses } from "../../query/classQuery";
import { useGetAllChildren } from "../../query/childQuery";
import { useGetAllActivities } from "../../query/activityQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import PostFeedCard from "./PostFeedCard";
import PostFeedFilters from "./PostFeedFilters";
import "../teachers/teachers.css";
import "./posts.css";

const EMPTY_FILTERS = { type: "", classroom: "", child: "", activity: "" };

function pageWindow(current, totalPages) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3];
  if (current >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];
  return [current - 1, current, current + 1];
}

function Posts() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [feedFilters, setFeedFilters] = useState(EMPTY_FILTERS);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: "10",
    keyword: "",
  });
  const debouncedKeyword = useDebouncedValue(searchTerm.trim(), 400);

  const { data: classesData, isLoading: loadingClasses } = useGetAllClasses({
    page: "1",
    limit: "100",
    status: "ACTIVE",
  });
  const { data: childrenData, isLoading: loadingChildren } = useGetAllChildren({
    page: "1",
    limit: "200",
    status: "ACTIVE",
  });
  const { data: activitiesData, isLoading: loadingActivities } = useGetAllActivities({
    page: "1",
    limit: "100",
    status: "ACTIVE",
  });

  const classList = useMemo(() => classesData?.docs || [], [classesData]);
  const childList = useMemo(() => childrenData?.docs || [], [childrenData]);
  const activityList = useMemo(() => activitiesData?.docs || [], [activitiesData]);

  const queryParams = useMemo(
    () => ({
      page: String(paginationConfig.pageNumber),
      limit: String(paginationConfig.limit),
      keyword: paginationConfig.keyword,
      ...(feedFilters.type && { type: feedFilters.type }),
      ...(feedFilters.classroom && { classroom: feedFilters.classroom }),
      ...(feedFilters.child && { child: feedFilters.child }),
      ...(feedFilters.activity && { activity: feedFilters.activity }),
    }),
    [paginationConfig, feedFilters]
  );

  const { isLoading, isFetching, data: postsData } = useGetAllPosts(queryParams);
  const { mutate: deletePost } = useDeletePost();

  const docs = postsData?.docs || [];
  const pageSize = Number(paginationConfig.limit) || 10;
  const currentPage = Number(paginationConfig.pageNumber) || 1;
  const totalDocs = postsData?.totalDocs || 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const from = totalDocs === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalDocs);

  useEffect(() => {
    setPaginationConfig((prev) =>
      prev.keyword === debouncedKeyword ? prev : { ...prev, keyword: debouncedKeyword, pageNumber: 1 }
    );
  }, [debouncedKeyword]);

  const handleFilterChange = (next) => {
    setFeedFilters(next);
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const hasFeedFilters = Boolean(
    feedFilters.type || feedFilters.classroom || feedFilters.child || feedFilters.activity
  );
  const hasQuery = Boolean(paginationConfig.keyword || hasFeedFilters);

  const activeFilterLabel = useMemo(() => {
    const parts = [];
    const typeIds = String(feedFilters.type || "").split(",").filter(Boolean);
    const activityIds = String(feedFilters.activity || "").split(",").filter(Boolean);
    const classIds = String(feedFilters.classroom || "").split(",").filter(Boolean);
    const childIds = String(feedFilters.child || "").split(",").filter(Boolean);

    if (typeIds.includes("CLASS") && typeIds.includes("CHILD")) parts.push("class and child posts");
    else if (typeIds.includes("CLASS")) parts.push("class posts");
    else if (typeIds.includes("CHILD")) parts.push("child posts");

    if (activityIds.length === 1) {
      const match = activityList.find((item) => String(item._id) === activityIds[0]);
      parts.push(match?.title || "1 activity");
    } else if (activityIds.length > 1) {
      parts.push(`${activityIds.length} activities`);
    }

    if (classIds.length === 1) {
      const match = classList.find((item) => String(item._id) === classIds[0]);
      parts.push(match?.classroomName || "1 class");
    } else if (classIds.length > 1) {
      parts.push(`${classIds.length} classes`);
    }

    if (childIds.length === 1) {
      const match = childList.find((item) => String(item._id) === childIds[0]);
      parts.push(
        match ? `${match.firstName || ""} ${match.lastName || ""}`.trim() : "1 student"
      );
    } else if (childIds.length > 1) {
      parts.push(`${childIds.length} students`);
    }

    return parts.length ? parts.join(" · ") : null;
  }, [feedFilters, classList, childList, activityList]);

  const handleDelete = (item) => {
    swal({
      title: "Are you sure?",
      text: "Do you want to delete this post?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deletePost(item._id, {
        onSuccess: () => swal("Success", "Post deleted successfully", "success"),
        onError: (error) => swal("Cannot delete", error?.message || "Failed to delete post", "error"),
      });
    });
  };

  return (
    <div className="data-page posts-feed-page">
      <div className="posts-layout">
        <div className="posts-sidebar-slot">
          <PostFeedFilters
            filters={feedFilters}
            onChange={handleFilterChange}
            activities={activityList}
            classes={classList}
            children={childList}
            loadingActivities={loadingActivities}
            loadingClasses={loadingClasses}
            loadingChildren={loadingChildren}
          />
        </div>

        <div className="posts-main">
          <div className="posts-main-toolbar">
            <div className="data-toolbar">
              <div className="data-toolbar-search">
                <label className="data-search">
                  <Search size={16} />
                  <input
                    placeholder="Search posts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
                </label>
              </div>
              <button type="button" className="data-primary" onClick={() => navigate("/posts/add")} aria-label="Add post">
                <Plus size={18} />
                Add post
              </button>
            </div>

            {activeFilterLabel ? (
              <div className="posts-active-filter">
                <span>
                  Showing posts for <strong>{activeFilterLabel}</strong>
                </span>
                <button type="button" onClick={() => handleFilterChange(EMPTY_FILTERS)}>
                  Clear filter
                </button>
              </div>
            ) : null}
          </div>

          {isLoading && !postsData ? (
            <div className="posts-feed">
              {[1, 2, 3].map((key) => (
                <div key={key} className="posts-feed-card is-skeleton">
                  <Skeleton active paragraph={{ rows: 4 }} />
                </div>
              ))}
            </div>
          ) : docs.length === 0 ? (
            <div className="posts-feed-empty data-empty">
              <span className="data-empty-icon">
                {hasQuery ? (
                  <SearchX size={36} strokeWidth={1.6} />
                ) : (
                  <Newspaper size={36} strokeWidth={1.6} />
                )}
              </span>
              <h3>{hasQuery ? "No posts found" : "No posts yet"}</h3>
              <p>
                {hasQuery
                  ? activeFilterLabel
                    ? `No posts match ${activeFilterLabel}. Try another filter or search term.`
                    : "Try a different keyword or filter."
                  : "Posts shared by teachers will appear here."}
              </p>
              {hasQuery && hasFeedFilters ? (
                <button
                  type="button"
                  className="posts-empty-clear"
                  onClick={() => handleFilterChange(EMPTY_FILTERS)}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <div className={`posts-feed${isFetching ? " is-fetching" : ""}`}>
                {docs.map((item) => (
                  <PostFeedCard
                    key={item._id}
                    post={item}
                    activities={activityList}
                    onDelete={handleDelete}
                  />
                ))}
              </div>

              <div className="posts-feed-pager data-pager">
                <p className="data-pager-text">
                  Showing {from}-{to} of {totalDocs} posts
                </p>
                <div className="data-pages">
                  <button
                    type="button"
                    className="data-page-btn is-nav"
                    disabled={currentPage <= 1}
                    onClick={() =>
                      setPaginationConfig((prev) => ({ ...prev, pageNumber: Math.max(1, currentPage - 1) }))
                    }
                  >
                    <ChevronLeft size={18} />
                  </button>
                  {pageWindow(currentPage, totalPages).map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`data-page-btn${page === currentPage ? " is-active" : ""}`}
                      onClick={() => setPaginationConfig((prev) => ({ ...prev, pageNumber: page }))}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="data-page-btn is-nav"
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setPaginationConfig((prev) => ({
                        ...prev,
                        pageNumber: Math.min(totalPages, currentPage + 1),
                      }))
                    }
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Posts;
